import { type RefObject, useEffect, useRef, useState } from "react";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";

/**
 * A field the attorney is typing into, before what they type has been stored.
 *
 * Six editors grew their own, and two grew only half: the position form and
 * the open row kept a draft with no guard, so amending a position and closing
 * the tab lost the words while doing the same to an element asked first.
 * Holding the two together is what stops the next editor arriving with one.
 *
 * Focus is asked for, not assumed: an editor replacing what it edits wants the
 * caret, a form already on the page does not steal it. `dirty` defaults to
 * identity, which is what a string draft wants; an object draft is rebuilt on
 * every keystroke, so it says how an edited one differs.
 */
export function useDraftEditor<T, E extends HTMLElement = HTMLTextAreaElement>(
  initial: T,
  {
    focusOnOpen = false,
    dirty = (draft: T, from: T) => draft !== from,
  }: {
    focusOnOpen?: boolean;
    dirty?: (draft: T, from: T) => boolean;
  } = {},
): {
  draft: T;
  setDraft: React.Dispatch<React.SetStateAction<T>>;
  dirty: boolean;
  fieldRef: RefObject<E | null>;
} {
  const [draft, setDraft] = useState<T>(initial);
  const fieldRef = useRef<E>(null);

  // Follow `initial` while the field is untouched, and keep typing otherwise.
  // An editor that unmounts when it closes never sees this; the open position
  // row does, since it stays mounted while the server echoes the save back,
  // and without it the field would show the words it was opened with for ever.
  // The catch-up when the two agree is that save landing: without it the field
  // stops following after the first edit. Same rule as `useAdoptedDraft`, which
  // is the string-only version of this.
  const adoptedRef = useRef<T>(initial);
  if (!dirty(initial, draft)) {
    adoptedRef.current = initial;
  } else if (
    dirty(initial, adoptedRef.current) &&
    !dirty(draft, adoptedRef.current)
  ) {
    adoptedRef.current = initial;
    setDraft(initial);
  }

  const unsaved = dirty(draft, initial);

  useUnsavedDraft(unsaved);

  // Once, when the editor opens. Read through a ref so following the prop
  // cannot move the caret back under an attorney who is already typing.
  const focusRef = useRef(focusOnOpen);
  useEffect(() => {
    if (focusRef.current) fieldRef.current?.focus();
  }, []);

  return { draft, setDraft, dirty: unsaved, fieldRef };
}
